import { type InvoiceFormOptions } from '@utazzo/shared';
import {
  UzButton,
  UzCard,
  UzCheckbox,
  UzDatePicker,
  UzNumberInput,
  UzSelect,
  type UzSelectOption,
} from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo } from 'react';

import { formatInrPaise } from '../invoices.helpers';

import { emptyDepositRow, rupeesToPaiseNum, type DepositRow } from './invoice-form.helpers';

interface DepositPlanProps {
  options: InvoiceFormOptions | undefined;
  paymentMode: string;
  onPaymentMode: (v: string) => void;
  clientPayingDate: Date | null;
  onClientPayingDate: (v: Date | null) => void;
  rows: DepositRow[];
  onChange: (rows: DepositRow[]) => void;
  grandTotalPaise: number | null;
}

/**
 * Section 10 - Payment info. Up to 4 deposit tranches; the running deposits
 * total must equal the grand total (from the live GST preview). We warn inline
 * on a mismatch but only block submit when the operator has actually entered a
 * deposit (an all-blank deposit plan is allowed).
 */
export const DepositPlan = ({
  options,
  paymentMode,
  onPaymentMode,
  clientPayingDate,
  onClientPayingDate,
  rows,
  onChange,
  grandTotalPaise,
}: DepositPlanProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<DepositRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const paymentModeOptions = useMemo<ReadonlyArray<UzSelectOption<string>>>(
    () => (options?.paymentModes ?? []).map((m) => ({ value: m, label: m })),
    [options?.paymentModes],
  );

  const depositsTotalPaise = rows.reduce((sum, r) => sum + rupeesToPaiseNum(r.amount), 0);
  const anyDeposit = rows.some((r) => r.amount !== '' && r.amount !== 0);
  const mismatch = anyDeposit && grandTotalPaise !== null && depositsTotalPaise !== grandTotalPaise;

  return (
    <UzCard
      title="Payment info"
      description="How and when the customer pays. Deposits must total the grand total."
      actions={
        <UzButton
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          disabled={rows.length >= 4}
          onClick={() => onChange([...rows, emptyDepositRow()])}
        >
          Add tranche
        </UzButton>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <UzSelect
            label="Payment mode"
            placeholder="Select payment mode"
            options={paymentModeOptions}
            value={paymentMode || undefined}
            onValueChange={onPaymentMode}
          />
          <UzDatePicker
            label="Client paying date"
            value={clientPayingDate}
            onValueChange={onClientPayingDate}
          />
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-zinc-500">No deposit tranches added.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row, idx) => (
              <div key={idx} className="grid items-start gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
                <UzNumberInput
                  label={idx === 0 ? 'Amount (Rs)' : undefined}
                  min={0}
                  value={row.amount}
                  onValueChange={(v) => update(idx, { amount: v ?? '' })}
                />
                <UzDatePicker
                  label={idx === 0 ? 'Balance due date' : undefined}
                  value={row.balanceDueDate}
                  onValueChange={(v) => update(idx, { balanceDueDate: v })}
                />
                <div className={idx === 0 ? 'md:pt-8' : 'md:pt-2'}>
                  <UzCheckbox
                    label="Paid"
                    checked={row.isPaid}
                    onCheckedChange={(c) => update(idx, { isPaid: Boolean(c) })}
                  />
                </div>
                <div className={idx === 0 ? 'md:pt-6' : ''}>
                  <UzButton
                    variant="ghost"
                    size="sm"
                    aria-label="Remove tranche"
                    onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </UzButton>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-6 border-t border-zinc-100 pt-3 text-sm">
          <span className="text-zinc-500">
            Deposits total:{' '}
            <span className="font-medium text-zinc-800 tabular-nums">
              {formatInrPaise(String(depositsTotalPaise))}
            </span>
          </span>
          <span className="text-zinc-500">
            Grand total:{' '}
            <span className="font-medium text-zinc-800 tabular-nums">
              {grandTotalPaise === null ? '-' : formatInrPaise(String(grandTotalPaise))}
            </span>
          </span>
        </div>
        {mismatch && (
          <p className="text-xs text-amber-600">
            Deposits total does not equal the grand total. Adjust the tranches before submitting.
          </p>
        )}
      </div>
    </UzCard>
  );
};
